// Admin Dashboard Component

import api from '../api.js';
import { renderStatsCard, STATS_ICONS } from './StatsCard.js';
import { renderUserTable, initUserTable, updateUserTable } from './UserTable.js';

// Dashboard state
let dashboardData = null;
let isLoading = true;
let error = null;

/**
 * Render the admin dashboard
 * @returns {string} HTML markup
 */
export function renderAdminDashboard() {
  return `
    <div class="admin-dashboard">
      <div id="stats-cards" class="stats-cards-grid">
        ${renderLoadingCards()}
      </div>
      <div id="users-section" class="users-section">
        <h2>Benutzerliste</h2>
        <div id="user-table-container">
          <p class="loading">Lade Benutzer...</p>
        </div>
      </div>
    </div>
  `;
}

/**
 * Render loading placeholder cards
 * @returns {string} HTML markup
 */
function renderLoadingCards() {
  return `
    <div class="stats-card loading-card">
      <div class="loading-placeholder"></div>
    </div>
    <div class="stats-card loading-card">
      <div class="loading-placeholder"></div>
    </div>
    <div class="stats-card loading-card">
      <div class="loading-placeholder"></div>
    </div>
    <div class="stats-card loading-card">
      <div class="loading-placeholder"></div>
    </div>
  `;
}

/**
 * Render stats cards with data
 * @param {Object} stats - Statistics data
 * @returns {string} HTML markup
 */
function renderStatsCards(stats) {
  if (!stats) return renderLoadingCards();

  return `
    ${renderStatsCard({
      icon: STATS_ICONS.users,
      label: 'Gesamtnutzer',
      value: stats.total_users,
      className: 'stats-card-users'
    })}
    ${renderStatsCard({
      icon: STATS_ICONS.bookmarks,
      label: 'Gesamte Bookmarks',
      value: stats.total_bookmarks,
      className: 'stats-card-bookmarks'
    })}
    ${renderStatsCard({
      icon: STATS_ICONS.newUsers,
      label: 'Neue Nutzer (7 Tage)',
      value: stats.new_users_7d,
      className: 'stats-card-new'
    })}
    ${renderStatsCard({
      icon: STATS_ICONS.activeUsers,
      label: 'Aktive Nutzer (30 Tage)',
      value: stats.active_users_30d,
      className: 'stats-card-active'
    })}
  `;
}

/**
 * Render error state
 * @param {string} message - Error message
 * @returns {string} HTML markup
 */
function renderError(message) {
  return `
    <div class="admin-error">
      <div class="error-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <h3>Fehler beim Laden</h3>
      <p>${escapeHtml(message)}</p>
      <button id="retry-btn" class="btn btn-primary">Erneut versuchen</button>
    </div>
  `;
}

/**
 * Initialize the admin dashboard
 */
export async function initAdminDashboard() {
  await loadDashboardData();
}

/**
 * Load dashboard data from API
 */
async function loadDashboardData() {
  isLoading = true;
  error = null;

  const statsContainer = document.getElementById('stats-cards');
  const usersContainer = document.getElementById('user-table-container');

  try {
    // Fetch combined dashboard data
    dashboardData = await api.getAdminDashboard();
    isLoading = false;

    // Update stats cards
    if (statsContainer) {
      statsContainer.innerHTML = renderStatsCards(dashboardData.stats);
    }

    // Update user table
    if (usersContainer && dashboardData.users) {
      usersContainer.innerHTML = renderUserTable(dashboardData.users);
      initUserTable(dashboardData.users, handlePageChange, handleSortChange);
    }
  } catch (err) {
    isLoading = false;
    error = err.message || 'Ein unerwarteter Fehler ist aufgetreten';
    console.error('Failed to load dashboard data:', err);

    // Show error state
    if (statsContainer) {
      statsContainer.innerHTML = '';
    }
    if (usersContainer) {
      usersContainer.innerHTML = renderError(error);

      // Add retry button handler
      document.getElementById('retry-btn')?.addEventListener('click', () => {
        if (statsContainer) {
          statsContainer.innerHTML = renderLoadingCards();
        }
        if (usersContainer) {
          usersContainer.innerHTML = '<p class="loading">Lade Benutzer...</p>';
        }
        loadDashboardData();
      });
    }
  }
}

/**
 * Handle page change in user table
 * @param {number} page - New page number
 * @param {number} size - Page size
 */
async function handlePageChange(page, size) {
  await loadUsers({ page, size });
}

/**
 * Handle sort change in user table
 * @param {string} sortBy - Column to sort by
 * @param {string} sortOrder - Sort direction (asc/desc)
 */
async function handleSortChange(sortBy, sortOrder) {
  await loadUsers({ sort_by: sortBy, sort_order: sortOrder });
}

/**
 * Load users with parameters
 * @param {Object} params - Query parameters
 */
async function loadUsers(params = {}) {
  const usersContainer = document.getElementById('user-table-container');
  if (!usersContainer) return;

  // Get current state from URL hash
  const currentParams = getParamsFromHash();
  const mergedParams = { ...currentParams, ...params };

  // Update URL hash with new params
  updateHashWithParams(mergedParams);

  try {
    const users = await api.getAdminUsers(mergedParams);
    updateUserTable(users, handlePageChange, handleSortChange);
  } catch (err) {
    console.error('Failed to load users:', err);
    usersContainer.innerHTML = renderError(err.message || 'Fehler beim Laden der Benutzer');

    document.getElementById('retry-btn')?.addEventListener('click', () => {
      usersContainer.innerHTML = '<p class="loading">Lade Benutzer...</p>';
      loadUsers(mergedParams);
    });
  }
}

/**
 * Get pagination/sort params from URL hash
 * @returns {Object} Parameters
 */
function getParamsFromHash() {
  const hash = window.location.hash;
  const queryStart = hash.indexOf('?');
  if (queryStart === -1) return {};

  const queryString = hash.substring(queryStart + 1);
  const params = new URLSearchParams(queryString);
  const result = {};

  for (const [key, value] of params) {
    result[key] = value;
  }

  return result;
}

/**
 * Update URL hash with parameters
 * @param {Object} params - Parameters to set
 */
function updateHashWithParams(params) {
  const cleanParams = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      cleanParams[key] = value;
    }
  }

  const queryString = new URLSearchParams(cleanParams).toString();
  const newHash = queryString ? `#/admin?${queryString}` : '#/admin';

  // Update without triggering hashchange if same path
  if (window.location.hash !== newHash) {
    history.replaceState(null, '', newHash);
  }
}

/**
 * Escape HTML special characters
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
